/**
 * =============================================================================
 * CONTEXT PILLS - SHARED COMPONENT
 * =============================================================================
 *
 * Renders a row of small context badges showing the filters an alert was
 * evaluated with.
 *
 * Fully dynamic — renders a pill for every key-value pair in the filters
 * object. Internal/API-only params are skipped via EXCLUDED_KEYS.
 *
 * Used by: WatchCard (Top Stories), AISpotlightCard, TodoStack (Pending Actions)
 */

import { Group, Badge } from '@mantine/core';
import {
    Storefront,
    Package,
    Tag,
    Calendar,
    FunnelSimple,
} from '@phosphor-icons/react';
import { memo } from 'react';

/** Any plain object from alert context / sourceParams */
export type ContextPillFilters = Record<string, unknown>;

// Keys that are API internals, never meaningful to show the user
const EXCLUDED_KEYS = new Set([
    'merchantId',
    'merchant_id',
    'page',
    'pageSize',
    'page_size',
    'sortBy',
    'sort_by',
    'search',
    'isActive',
    'is_active',
    'productIds',
    'product_ids',
    'productGroups',
    'product_groups',
]);

// Well-known keys get a dedicated icon; everything else gets FunnelSimple
const ICON_MAP: Record<string, React.ReactNode> = {
    channels: <Storefront size={10} weight="fill" />,
    vendors: <Package size={10} weight="fill" />,
    type: <Tag size={10} weight="fill" />,
    categories: <Tag size={10} weight="fill" />,
};

const genericIcon = <FunnelSimple size={10} weight="fill" />;

/** Convert a snake/camel key to a short human label, e.g. under_stock_limit → "limit" */
const humanizeKey = (key: string): string => {
    return key
        .replace(/_limit$/, ' limit')
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .toLowerCase();
};

interface ContextPillsProps {
    filters?: ContextPillFilters;
}

const ContextPills: React.FC<ContextPillsProps> = memo(({ filters }) => {
    if (!filters) return null;

    const pills: { icon: React.ReactNode; value: string }[] = [];

    // Handle startDate + endDate as a combined date-range pill
    const startDate = filters.startDate as string | undefined;
    const endDate = filters.endDate as string | undefined;

    for (const [key, val] of Object.entries(filters)) {
        if (EXCLUDED_KEYS.has(key)) continue;
        if (val === null || val === undefined) continue;

        // Date range — handled as a single combined pill below
        if (key === 'startDate' || key === 'endDate') continue;

        let displayValue: string;

        if (Array.isArray(val)) {
            if (val.length === 0) continue;
            displayValue = (val as unknown[]).map(String).join(', ');
        } else if (typeof val === 'string') {
            if (!val) continue;
            displayValue = val;
        } else if (typeof val === 'number') {
            displayValue = `${humanizeKey(key)}: ${val}`;
        } else {
            continue; // skip nested objects, booleans, etc.
        }

        pills.push({
            icon: ICON_MAP[key] ?? genericIcon,
            value: displayValue,
        });
    }

    // Date range pill
    if (startDate && endDate) {
        const fmt = (d: string) => {
            try {
                return new Date(d).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                });
            } catch {
                return d;
            }
        };
        pills.push({
            icon: <Calendar size={10} weight="fill" />,
            value: `${fmt(startDate)} – ${fmt(endDate)}`,
        });
    }

    if (pills.length === 0) return null;

    return (
        <Group style={{ gap: 4, marginTop: 6, flexWrap: 'wrap' }}>
            {pills.map((pill, idx) => (
                <Badge
                    key={idx}
                    size="xs"
                    variant="light"
                    color="gray"
                    leftSection={pill.icon}
                    styles={{
                        root: {
                            backgroundColor: 'rgba(0,0,0,0.03)',
                            border: 'none',
                            paddingLeft: 6,
                            paddingRight: 8,
                        },
                        label: {
                            fontSize: 10,
                            fontWeight: 500,
                            color: 'rgb(104, 104, 104)',
                        },
                    }}
                >
                    {pill.value}
                </Badge>
            ))}
        </Group>
    );
});

ContextPills.displayName = 'ContextPills';

export default ContextPills;
